import { useRef, useState, useEffect } from 'react'
import { ChevronLeft, ChevronRight, Sparkles, Package, TrendingUp, Clock, Heart, ThumbsUp } from 'lucide-react'
import { ProductIcon, getCategoryIcon } from '@/lib/icons'
import { useCategories } from '@/api/products'
import { usePOSStore } from '@/stores/posStore'
import { useIsMobile } from '@/hooks'

interface Category {
  id: number | null
  name: string
  icon: string
}

// Special negative IDs for mobile quick-access pills
const TRENDING_ID = -2
const RECENT_ID = -3
const FAVOURITES_ID = -4
const RECOMMENDED_ID = -5

export function CategoriesBar() {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  const { selectedCategoryId, selectedCustomer, setSelectedCategory } = usePOSStore()
  const { data: apiCategories = [] } = useCategories()
  const isMobile = useIsMobile()

  // Build mobile quick-access pills
  const mobileQuickAccess: Category[] = isMobile
    ? [
        { id: TRENDING_ID, name: 'Trending', icon: 'TrendingUp' },
        ...(selectedCustomer
          ? [
              { id: RECENT_ID, name: 'Recent', icon: 'Clock' },
              { id: FAVOURITES_ID, name: 'Favourites', icon: 'Heart' },
              { id: RECOMMENDED_ID, name: 'Recommended', icon: 'ThumbsUp' },
            ]
          : []),
      ]
    : []

  // Build categories list with All and On Sale at the front
  const categories: Category[] = [
    { id: null, name: 'All Products', icon: 'Package' },
    { id: -1, name: 'On Sale', icon: 'Sparkles' },
    ...mobileQuickAccess,
    ...apiCategories.map(c => ({
      id: c.id,
      name: c.name || 'Unknown',
      icon: getCategoryIcon(c.name || ''),
    })),
  ]

  const checkScroll = () => {
    const el = scrollRef.current
    if (!el) return
    setCanScrollLeft(el.scrollLeft > 0)
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 1)
  }

  useEffect(() => {
    checkScroll()
    const el = scrollRef.current
    if (el) {
      el.addEventListener('scroll', checkScroll)
      window.addEventListener('resize', checkScroll)
    }
    return () => {
      if (el) el.removeEventListener('scroll', checkScroll)
      window.removeEventListener('resize', checkScroll)
    }
  }, [apiCategories, isMobile, selectedCustomer])

  const scrollCategories = (direction: 'left' | 'right') => {
    const el = scrollRef.current
    if (!el) return
    const scrollAmount = direction === 'left' ? -200 : 200
    el.scrollBy({ left: scrollAmount, behavior: 'smooth' })
  }

  const renderIcon = (iconName: string) => {
    switch (iconName) {
      case 'Sparkles': return <Sparkles size={16} />
      case 'Package': return <Package size={16} />
      case 'TrendingUp': return <TrendingUp size={16} />
      case 'Clock': return <Clock size={16} />
      case 'Heart': return <Heart size={16} />
      case 'ThumbsUp': return <ThumbsUp size={16} />
      default: return <ProductIcon name={iconName} size={16} />
    }
  }

  return (
    <div className="bg-white border-b border-gray-200 py-3">
      <div className="relative flex items-center">
        {canScrollLeft && (
          <button
            onClick={() => scrollCategories('left')}
            className="absolute left-2 top-1/2 -translate-y-1/2 z-10 bg-white shadow-md rounded-full w-9 h-9 flex items-center justify-center border border-gray-200 cursor-pointer hover:bg-gray-50"
          >
            <ChevronLeft size={16} className="text-gray-600" />
          </button>
        )}

        <div
          ref={scrollRef}
          className="flex items-center gap-2 overflow-x-auto px-4"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {categories.map((cat) => {
            const isSelected = selectedCategoryId === cat.id

            return (
              <button
                key={cat.id ?? 'all'}
                onClick={() => setSelectedCategory(cat.id)}
                className={`flex items-center gap-1.5 px-3 py-2 md:py-1.5 rounded-full whitespace-nowrap transition-all cursor-pointer text-sm ${
                  isSelected
                    ? 'bg-green-500 text-white'
                    : 'bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-700'
                }`}
              >
                {renderIcon(cat.icon)}
                <span className="font-medium">{cat.name}</span>
              </button>
            )
          })}
        </div>

        {canScrollRight && (
          <button
            onClick={() => scrollCategories('right')}
            className="absolute right-2 top-1/2 -translate-y-1/2 z-10 bg-white shadow-md rounded-full w-9 h-9 flex items-center justify-center border border-gray-200 cursor-pointer hover:bg-gray-50"
          >
            <ChevronRight size={16} className="text-gray-600" />
          </button>
        )}
      </div>
    </div>
  )
}
